import React from 'react';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';
import StarRating from '../../../components/ui/StarRating';
import RewardBadge from '../../../components/ui/RewardBadge';

const TimeSlot = ({ 
  time, 
  tasks, 
  onTaskComplete, 
  onTaskDelete,
  onTaskDragStart,
  onTaskDrop,
  onTaskDragOver,
  persons 
}) => {
  const formatTime = (hour) => {
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:00 ${period}`;
  };

  const getPersonColor = (personId) => {
    const person = persons?.find(p => p?.id === personId);
    return person?.color || '#6B7280';
  };

  const getPersonName = (personId) => {
    const person = persons?.find(p => p?.id === personId);
    return person?.name || 'Unknown';
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    if (onTaskDragOver) {
      onTaskDragOver(time);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    if (onTaskDrop) {
      onTaskDrop(time);
    }
  };

  return (
    <div 
      className="bg-card rounded-xl border border-border p-4 md:p-5 lg:p-6 transition-smooth hover:shadow-warm-md"
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <div className="flex items-center justify-between mb-3 md:mb-4">
        <div className="flex items-center gap-2 md:gap-3">
          <Icon name="clock" size={18} className="text-primary" />
          <h3 className="text-base md:text-lg lg:text-xl font-semibold text-foreground">
            {formatTime(time)}
          </h3>
        </div>
        <span className="text-xs md:text-sm text-muted-foreground">
          {tasks?.length} {tasks?.length === 1 ? 'task' : 'tasks'}
        </span>
      </div>
      <div className="space-y-2 md:space-y-3">
        {tasks?.length === 0 ? (
          <div className="text-center py-6 md:py-8 text-muted-foreground text-sm md:text-base">
            <Icon name="calendar-x" size={32} className="mx-auto mb-2 opacity-50" />
            <p>No tasks scheduled</p>
          </div>
        ) : (
          tasks?.map((task) => (
            <div
              key={task?.id}
              draggable
              onDragStart={() => onTaskDragStart && onTaskDragStart(task)}
              className="bg-background rounded-lg p-3 md:p-4 border-l-4 transition-smooth hover:shadow-warm-sm cursor-move"
              style={{ borderLeftColor: getPersonColor(task?.personId) }}
            >
              <div className="flex items-start gap-3">
                <Checkbox
                  checked={task?.completed}
                  onChange={(e) => onTaskComplete && onTaskComplete(task?.id, e?.target?.checked)}
                  className="mt-1"
                  size="default"
                />

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h4 className={`text-sm md:text-base font-medium ${task?.completed ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                      {task?.title}
                    </h4>
                    <button
                      onClick={() => onTaskDelete && onTaskDelete(task?.id)}
                      className="flex-shrink-0 p-1 hover:bg-destructive/10 rounded transition-smooth"
                      aria-label="Delete task"
                    >
                      <Icon name="trash-2" size={16} className="text-destructive" />
                    </button>
                  </div>

                  {task?.description && (
                    <p className="text-xs md:text-sm text-muted-foreground mb-2 line-clamp-2">
                      {task?.description}
                    </p>
                  )}

                  <div className="flex flex-wrap items-center gap-2 md:gap-3">
                    <div 
                      className="flex items-center gap-1.5 px-2 py-1 rounded-md text-xs md:text-sm font-medium"
                      style={{ 
                        backgroundColor: `${getPersonColor(task?.personId)}20`,
                        color: getPersonColor(task?.personId)
                      }}
                    >
                      <Icon name="user" size={14} />
                      <span>{getPersonName(task?.personId)}</span>
                    </div>

                    {task?.duration && (
                      <div className="flex items-center gap-1 text-xs md:text-sm text-muted-foreground">
                        <Icon name="timer" size={14} />
                        <span>{task?.duration} min</span>
                      </div>
                    )}

                    {task?.priority && (
                      <div className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs md:text-sm font-medium ${
                        task?.priority === 'high' ? 'bg-destructive/10 text-destructive' :
                        task?.priority === 'medium'? 'bg-warning/10 text-warning' : 'bg-muted text-muted-foreground'
                      }`}>
                        <Icon name="flag" size={14} />
                        <span className="capitalize">{task?.priority}</span>
                      </div>
                    )}

                    {task?.stars > 0 && (
                      <div className="flex items-center gap-1.5">
                        <StarRating value={task?.stars} readonly size={14} />
                      </div>
                    )}

                    {task?.reward && (
                      <RewardBadge reward={task?.reward} size="sm" showLabel={false} />
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default TimeSlot;